import Component from 'flarum/common/Component';
import icon from 'flarum/common/helpers/icon';
import Button from 'flarum/common/components/Button';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import humanTime from 'flarum/common/utils/humanTime';
import renderMentions from '../renderMentions';
import StoryPoll from './StoryPoll';
import StoryReplyBar from './StoryReplyBar';
import StoryReplyList from './StoryReplyList';
import { REACTIONS, emojiFor } from '../reactions';

/**
 * Fullscreen story viewer modal.
 *
 * Navigation is two-dimensional, like Instagram: it walks the current user's
 * stories, then moves on to the next user's, and only closes after the last
 * story of the last user.
 *
 * Props:
 *   groups       – array of {user, isViewed, stories[]}
 *   startGroup   – which user group to open first
 *   startStory   – which story of that group to start at (default 0)
 *   onclose      – callback
 *   onliketoggle – callback(groupIdx, storyIdx, result)
 *   onviewed     – callback(groupIdx, storyIdx, result)
 *   ondelete     – callback(groupIdx, storyIdx)
 */
/**
 * Silence a video for good as it leaves the page.
 *
 * Removing a playing <video> from the document is supposed to pause it, but
 * not every browser does — iOS Safari in particular can keep the sound going
 * after the viewer has moved on or closed. Pausing and dropping the source
 * stops it everywhere and frees the download too.
 */
function stopVideo(video) {
  if (!video) return;

  try {
    video.pause();
    video.removeAttribute('src');
    video.load();
  } catch (e) {
    // Nothing to stop.
  }
}

export default class StoryViewer extends Component {
  oninit(vnode) {
    super.oninit(vnode);

    this.groupIndex = this.attrs.startGroup || 0;
    const group = this.attrs.groups[this.groupIndex];
    const start = this.attrs.startStory || 0;
    this.storyIndex = group && start < group.stories.length ? start : 0;
    this.progress     = 0; // 0 to 100
    this.paused       = false;
    const seconds = parseInt(app.forum.attribute('happydigital-stories.viewDurationSeconds'), 10);
    this.duration = (seconds > 0 ? seconds : 20) * 1000; // per-story display time (ms)
    this.timer        = null;
    this.tickInterval = null;
    this.startTime    = null;

    this.showViewersList = false;
    // 'all' for the owner's full panel, 'likers' when opened from the heart.
    this.listMode        = 'all';
    this.reactionsOpen   = false;

    // Urls whose bytes are in, so a picture already seen is not covered by a
    // spinner again when the viewer steps back to it.
    this.loadedImages = new Set();

    // Video playback. Each story gets a fresh <video> element, so readiness is
    // tracked per story id rather than cached like picture urls.
    this.videoEl      = null;
    this.readyVideoId = null;
    this.brokenVideos = new Set();
    // Sound is tried first; muted is the fallback when the browser refuses
    // autoplay with sound, and then sticks for the rest of the session.
    this.muted        = false;

    // Start timer on mount, unless the first story is a picture still loading
    // or a video, which starts itself from videoReady().
    if (!this.waitingForImage() && !this.playsVideo()) {
      this.startTimer();
    }

    this.markCurrentViewed();
    this.preloadAround();

    // Prevent body scroll
    document.body.classList.add('StoryViewer-open');
  }

  onremove(vnode) {
    super.onremove(vnode);
    this.stopTimer();
    // The video's own onremove does this too; closing is where a stray sound
    // would be most noticeable, so it is not left to that alone.
    stopVideo(this.videoEl);
    document.body.classList.remove('StoryViewer-open');
  }

  /** The user group currently being played. */
  currentGroup() {
    return this.attrs.groups[this.groupIndex];
  }

  /** The story currently on screen. */
  currentStory() {
    const group = this.currentGroup();
    return group && group.stories[this.storyIndex];
  }

  /**
   * Close the viewer and navigate to the story owner's profile page.
   */
  goToProfile(user) {
    if (!user || !user.username) return;

    this.attrs.onclose();
    m.route.set(app.route('user', { username: user.username }));
  }

  /**
   * Fire-and-forget click tracking; navigation proceeds regardless.
   */
  trackLinkClick(story) {
    app
      .request({
        method: 'POST',
        url: '/api/happydigital/stories/' + story.id + '/link-click',
      })
      .then((response) => {
        story.linkClicksCount = response.linkClicksCount;
        m.redraw();
      })
      .catch(() => {});
  }

  /**
   * Display label for the link button: the author's custom label,
   * or the bare domain as fallback (like Instagram).
   */
  linkLabel(story) {
    if (story.linkLabel) return story.linkLabel;

    try {
      return new URL(story.linkUrl).hostname.replace(/^www\./, '');
    } catch (e) {
      return app.translator.trans('happydigital-stories.forum.view_link');
    }
  }

  view() {
    const story = this.currentStory();
    if (!story) return <div />;

    const user = story.user;
    const isOwn = app.session.user && app.session.user.id() == user.id;

    let content;
    if (story.type === 'video') {
      content = this.videoContent(story);
    } else if (story.type === 'image') {
      const ready = this.loadedImages.has(story.imageUrl);

      // The container carries the key, not the picture inside it: Mithril
      // requires every child of a parent to be keyed or none of them, and the
      // spinner beside the picture has no key of its own. Keying the container
      // replaces the whole frame per story, so the <img> is rebuilt with it —
      // without that Mithril reused one element and only swapped src, and the
      // browser kept painting the previous picture until the new one arrived.
      content = (
        <div className="StoryViewer-imageContainer" key={story.imageUrl}>
          <img
            src={story.imageUrl}
            alt="Story"
            className={'StoryViewer-image' + (ready ? '' : ' is-loading')}
            onload={() => this.imageReady(story.imageUrl)}
            onerror={() => this.imageReady(story.imageUrl)}
          />
          {ready ? null : (
            <div className="StoryViewer-imageLoading">
              {LoadingIndicator.component({ display: 'unset' })}
            </div>
          )}
        </div>
      );
    } else {
      // Keyed like the image frame above: the two take turns in the same slot,
      // and Mithril rejects a switch between a keyed and an unkeyed vnode there.
      content = (
        <div
          className="StoryViewer-textContainer"
          key={'text-' + story.id}
          style={{ background: story.bgGradient }}
        >
          <div className="StoryViewer-textContent">
            {renderMentions(story.content, () => this.attrs.onclose())}
          </div>
        </div>
      );
    }

    return (
      <div className="StoryViewer"
           onmousedown={(e) => this.holdStart(e)}
           onmouseup={(e) => this.holdEnd(e)}
           ontouchstart={(e) => this.holdStart(e)}
           ontouchend={(e) => this.holdEnd(e)}>
        
        {/* Background dark overlay */}
        <div className="StoryViewer-overlay" onclick={() => this.attrs.onclose()} />

        <div className="StoryViewer-contentWrapper">
          
          {/* Progress Bars */}
          <div className="StoryViewer-progressContainer">
            {this.currentGroup().stories.map((s, idx) => {
              let fill = 0;
              if (idx < this.storyIndex) fill = 100;
              else if (idx === this.storyIndex) fill = this.progress;

              return (
                <div className="StoryViewer-progressBar" key={s.id}>
                  <div className="StoryViewer-progressFill" style={{ width: fill + '%' }} />
                </div>
              );
            })}
          </div>

          {/* Header */}
          <div className="StoryViewer-header">
            <div className="StoryViewer-userInfo" onclick={(e) => { e.stopPropagation(); this.goToProfile(user); }}>
              {user.avatarUrl
                ? <img className="StoryViewer-avatar" src={user.avatarUrl} alt={user.displayName} />
                : <div className="StoryViewer-avatarFallback">{(user.displayName || user.username || '?')[0].toUpperCase()}</div>
              }
              <div className="StoryViewer-userDetails">
                <span className="StoryViewer-username">{user.displayName || user.username}</span>
                <span className="StoryViewer-time">{humanTime(story.createdAt)}</span>
              </div>
            </div>

            <div className="StoryViewer-actions">
              {story.type === 'video' && (
                <Button
                  className="Button Button--icon Button--link StoryViewer-muteBtn"
                  icon={this.muted ? 'fas fa-volume-mute' : 'fas fa-volume-up'}
                  aria-label={app.translator.trans(
                    this.muted ? 'happydigital-stories.forum.video_unmute' : 'happydigital-stories.forum.video_mute'
                  )}
                  onmousedown={(e) => e.stopPropagation()}
                  ontouchstart={(e) => e.stopPropagation()}
                  onclick={(e) => { e.stopPropagation(); this.toggleMute(); }}
                />
              )}
              {story.canDelete && (
                <Button 
                  className="Button Button--icon Button--link StoryViewer-deleteBtn"
                  icon="fas fa-trash"
                  onclick={(e) => { e.stopPropagation(); this.deleteStory(story); }}
                />
              )}
              <Button 
                className="Button Button--icon Button--link StoryViewer-closeBtn"
                icon="fas fa-times"
                onclick={(e) => { e.stopPropagation(); this.attrs.onclose(); }}
              />
            </div>
          </div>

          {/* Nav Areas (Left/Right invisible clickable areas) */}
          <div className="StoryViewer-navArea StoryViewer-navLeft" onclick={(e) => { e.stopPropagation(); this.prevStory(); }} />
          <div className="StoryViewer-navArea StoryViewer-navRight" onclick={(e) => { e.stopPropagation(); this.nextStory(); }} />

          {/* Content */}
          <div className="StoryViewer-body">
            {content}
          </div>

          {/* Poll, if the story carries one */}
          {story.poll
            ? StoryPoll.component({
                story,
                // The story waited for this answer, so it resumes once given —
                // with a full run, since the countdown had already expired while
                // it waited and would otherwise skip on straight away.
                onvote: () => this.startTimer(),
              })
            : null}

          {/* Link button (Instagram-style) */}
          {story.linkUrl && (
            <a
              className="StoryViewer-linkBtn"
              href={story.linkUrl}
              target="_blank"
              rel="noopener noreferrer nofollow"
              onclick={(e) => { e.stopPropagation(); this.trackLinkClick(story); }}
              onmousedown={(e) => e.stopPropagation()}
              ontouchstart={(e) => e.stopPropagation()}
            >
              {icon('fas fa-link')} {this.linkLabel(story)}
            </a>
          )}

          {/* Footer (Likes & Viewers) */}
          <div className="StoryViewer-footer" onclick={(e) => e.stopPropagation()}>
            {/* A private reply to the author. Not for one's own story, and not
                for guests, who have no one to reply as. */}
            {!isOwn && app.session.user
              ? StoryReplyBar.component({
                  story,
                  onfocus: () => this.pauseTimer(),
                  onblur: () => this.resumeTimer(),
                })
              : null}

            {/* Reaction strip. Collapsed to one button so the footer stays as
                quiet as before; tapping it opens the choices. Whoever has
                already reacted sees their own emoji on the button, which is
                also how they take it back. */}
            <div className="StoryViewer-reactions">
              {this.reactionsOpen ? (
                <div className="StoryViewer-reactionStrip">
                  {REACTIONS.map((r) => (
                    <button
                      className={
                        'StoryViewer-reaction' +
                        (story.myReaction === r.key ? ' is-mine' : '')
                      }
                      type="button"
                      key={r.key}
                      title={app.translator.trans(
                        'happydigital-stories.forum.reaction_' + r.key
                      )}
                      aria-label={app.translator.trans(
                        'happydigital-stories.forum.reaction_' + r.key
                      )}
                      onclick={() => this.react(story, r.key)}
                    >
                      <span className="StoryViewer-reactionEmoji">{r.emoji}</span>
                      {story.reactions && story.reactions[r.key] > 0 ? (
                        <span className="StoryViewer-reactionCount">
                          {story.reactions[r.key]}
                        </span>
                      ) : null}
                    </button>
                  ))}
                </div>
              ) : null}

              <button
                className={
                  'Button Button--icon Button--link StoryViewer-likeBtn' +
                  (story.isLiked ? ' is-liked' : '')
                }
                type="button"
                title={app.translator.trans(
                  isOwn ? 'happydigital-stories.forum.likers_list' : 'happydigital-stories.forum.react'
                )}
                // The owner has nothing to react to on their own story; for
                // them the heart answers "who liked it" straight away.
                onclick={() => (isOwn ? this.openLikers() : this.toggleReactions())}
              >
                {story.myReaction ? (
                  <span className="StoryViewer-myReaction">
                    {emojiFor(story.myReaction)}
                  </span>
                ) : (
                  icon('far fa-heart')
                )}
                {story.likesCount > 0 ? (
                  <span className="StoryViewer-likeCount">{story.likesCount}</span>
                ) : null}
              </button>
            </div>

            {isOwn && (
              <div className="StoryViewer-viewersToggle" onclick={() => this.toggleViewersList()}>
                {icon('fas fa-eye')} {app.translator.trans('happydigital-stories.forum.viewers', { count: story.viewsCount })}
              </div>
            )}

            {isOwn && (
              <div className="StoryViewer-viewersToggle" onclick={() => this.toggleViewersList()}>
                {icon('fas fa-reply')} {app.translator.trans('happydigital-stories.forum.replies_count', { count: (story.replies || []).length })}
              </div>
            )}

            {isOwn && story.linkUrl && (
              <div className="StoryViewer-linkClicks">
                {icon('fas fa-link')} {app.translator.trans('happydigital-stories.forum.link_clicks', { count: story.linkClicksCount || 0 })}
              </div>
            )}
          </div>

          {/* Viewers List Drawer */}
          {this.showViewersList && isOwn && (
            <div className="StoryViewer-viewersList" onclick={(e) => e.stopPropagation()}>
              <div className="StoryViewer-viewersHeader">
                {this.listMode === 'likers' ? (
                  <h4>{icon('fas fa-heart')} {app.translator.trans('happydigital-stories.forum.likers_list')}</h4>
                ) : (
                  <h4>{icon('fas fa-reply')} / {icon('fas fa-eye')} / {icon('fas fa-heart')}</h4>
                )}
                <Button className="Button Button--icon Button--link" icon="fas fa-chevron-down" onclick={() => this.toggleViewersList()} />
              </div>
              {this.listMode === 'likers' ? (
              <div className="StoryViewer-viewersScroll">{this.likersSection(story, false)}</div>
              ) : (
              <div className="StoryViewer-viewersScroll">
                {/* Replies first: they are messages waiting to be read. */}
                {StoryReplyList.component({
                  replies: story.replies,
                  onuser: (u) => this.goToProfile(u),
                })}

                {/* Viewers */}
                <div style={{ marginBottom: '20px' }}>
                  <div style={{ fontWeight: 'bold', marginBottom: '10px', color: 'var(--text-color)' }}>{app.translator.trans('happydigital-stories.forum.viewers_list')}</div>
                  {(!story.viewers || story.viewers.length === 0) ? (
                    <div className="StoryViewer-noViewers">{app.translator.trans('happydigital-stories.forum.no_viewers')}</div>
                  ) : (
                    story.viewers.map(v => (
                      <div className="StoryViewer-viewerItem" key={v.id}>
                        {v.avatarUrl
                          ? <img className="StoryViewer-avatar" src={v.avatarUrl} alt={v.displayName} />
                          : <div className="StoryViewer-avatarFallback">{(v.displayName || v.username || '?')[0].toUpperCase()}</div>
                        }
                        <span className="StoryViewer-username">{v.displayName || v.username}</span>
                        {/* Guests arrive as one row carrying a count; they have
                            no identity and no meaningful view time. */}
                        {v.guestCount
                          ? <span className="StoryViewer-guestCount">{v.guestCount}</span>
                          : <span className="StoryViewer-time">{humanTime(v.viewedAt)}</span>
                        }
                      </div>
                    ))
                  )}
                </div>

                {this.likersSection(story, true)}
              </div>
              )}
            </div>
          )}

        </div>
      </div>
    );
  }

  /**
   * The video frame. Keyed per story, like the picture frame, so every story
   * gets its own element instead of one element whose src is swapped.
   */
  videoContent(story) {
    const ready = this.readyVideoId === story.id || this.brokenVideos.has(story.id);

    return (
      <div className="StoryViewer-videoContainer" key={'video-' + story.id}>
        <video
          className="StoryViewer-video"
          src={story.videoUrl}
          poster={story.videoPosterUrl || undefined}
          preload="auto"
          playsinline
          muted={this.muted}
          oncreate={(vnode) => (this.videoEl = vnode.dom)}
          onremove={(vnode) => {
            stopVideo(vnode.dom);
            if (this.videoEl === vnode.dom) this.videoEl = null;
          }}
          oncanplay={() => this.videoReady(story.id)}
          onerror={() => this.videoFailed(story.id)}
          onended={() => this.videoEnded(story.id)}
        />
        {ready ? null : (
          <div className="StoryViewer-imageLoading">
            {LoadingIndicator.component({ display: 'unset' })}
          </div>
        )}
      </div>
    );
  }

  /**
   * Who reacted, and with what, newest first. `withTitle` is false when the
   * panel was opened from the heart and its header already names the list.
   */
  likersSection(story, withTitle) {
    // Newest first, like the replies. A copy: the story's own list stays as
    // the server sent it.
    const likers = [...(story.likers || [])].sort((a, b) => String(b.likedAt).localeCompare(String(a.likedAt)));

    return (
      <div className="StoryViewer-listSection">
        {withTitle ? (
          <div className="StoryViewer-listTitle">
            {app.translator.trans('happydigital-stories.forum.likers_list')}
          </div>
        ) : null}

        {likers.length === 0 ? (
          <div className="StoryViewer-noViewers">{app.translator.trans('happydigital-stories.forum.no_likers')}</div>
        ) : (
          likers.map((l) => (
            <div className="StoryViewer-viewerItem" key={l.id}>
              {l.avatarUrl
                ? <img className="StoryViewer-avatar" src={l.avatarUrl} alt={l.displayName} />
                : <div className="StoryViewer-avatarFallback">{(l.displayName || l.username || '?')[0].toUpperCase()}</div>
              }
              <a className="StoryViewer-username" onclick={() => this.goToProfile(l)}>{l.displayName || l.username}</a>
              {l.reaction ? <span className="StoryViewer-likerReaction">{emojiFor(l.reaction)}</span> : null}
              <span className="StoryViewer-time">{humanTime(l.likedAt)}</span>
            </div>
          ))
        )}
      </div>
    );
  }

  // ── Hold to pause ─────────────────────────────────────────────────────────

  /**
   * Whether a press landed on a control rather than on the story itself.
   *
   * Holding the story pauses it, Instagram-style. Applied to every press, a
   * tap on the heart or any other button paused and resumed the video in
   * quick succession — a visible stutter and a break in the sound — and a
   * release on a button could resume a story the panel had meant to hold.
   */
  isControl(e) {
    const target = e && e.target;

    return !!(
      target &&
      target.closest &&
      target.closest('button, a, input, textarea, .StoryViewer-footer, .StoryViewer-header, .StoryViewer-viewersList, .StoryPollBox')
    );
  }

  holdStart(e) {
    if (!this.isControl(e)) this.pauseTimer();
  }

  holdEnd(e) {
    if (!this.isControl(e)) this.resumeTimer();
  }

  // ── Timer Logic ───────────────────────────────────────────────────────────

  /** Whether the current story is played by its video rather than the clock. */
  playsVideo() {
    const story = this.currentStory();

    return !!(story && story.type === 'video' && !this.brokenVideos.has(story.id));
  }

  /**
   * First playable moment of the current video: start its run. canplay fires
   * again after every stall, so only the first one counts.
   */
  videoReady(id) {
    const story = this.currentStory();

    if (!story || story.id !== id || this.readyVideoId === id) return;

    this.readyVideoId = id;
    this.startTimer();
  }

  /**
   * A video that cannot load falls back to the regular countdown over its
   * poster, rather than holding the viewer on a spinner.
   */
  videoFailed(id) {
    this.brokenVideos.add(id);

    const story = this.currentStory();

    if (story && story.id === id) this.startTimer();
  }

  videoEnded(id) {
    const story = this.currentStory();

    if (story && story.id === id && !this.paused) this.nextStory();
  }

  /** Play from the start, falling back to muted if sound is refused. */
  playVideo() {
    const video = this.videoEl;

    if (!video || !video.isConnected) return;

    video.currentTime = 0;
    video.muted = this.muted;

    const attempt = video.play();

    if (attempt && attempt.catch) {
      attempt.catch(() => {
        // Gone by the time the refusal came back: starting it now would play
        // a clip that is no longer on screen.
        if (video.muted || !video.isConnected) return;

        this.muted = true;
        video.muted = true;
        video.play().catch(() => {});
        m.redraw();
      });
    }
  }

  toggleMute() {
    this.muted = !this.muted;

    if (this.videoEl) this.videoEl.muted = this.muted;
  }


  /**
   * Note a picture as arrived, and start its story's run.
   *
   * Called for an error too: a picture that cannot load must not freeze the
   * story on a spinner forever — better to move on than to stall.
   */
  imageReady(url) {
    if (this.loadedImages.has(url)) return;

    this.loadedImages.add(url);

    // The countdown belongs to the picture being visible, not to the request.
    // On a slow connection the old code spent part of the story's time on a
    // blank frame.
    const story = this.currentStory();

    if (story && story.imageUrl === url) {
      this.startTimer();
    }

    m.redraw();
  }

  /**
   * Fetch the pictures on either side of the current story.
   *
   * Stepping forward is what people do most, so the next one matters most; the
   * previous one is worth having for a step back. Anything further away is not
   * fetched — on a phone that would spend someone's data on stories they may
   * never reach.
   */
  preloadAround() {
    const group = this.currentGroup();

    if (!group) return;

    const urls = [];
    const at = (idx) => {
      const story = group.stories[idx];

      if (story && story.type === 'image' && story.imageUrl) {
        urls.push(story.imageUrl);
      }

      // Only the poster: fetching video bytes ahead would spend someone's data
      // on a clip they may skip.
      if (story && story.type === 'video' && story.videoPosterUrl) {
        urls.push(story.videoPosterUrl);
      }
    };

    at(this.storyIndex + 1);
    at(this.storyIndex - 1);

    // The first story of the next user, for the moment this group runs out.
    const nextGroup = this.attrs.groups[this.groupIndex + 1];

    if (nextGroup && nextGroup.stories[0]) {
      const first = nextGroup.stories[0];

      if (first.type === 'image' && first.imageUrl) {
        urls.push(first.imageUrl);
      }
    }

    urls.forEach((url) => {
      if (this.loadedImages.has(url)) return;

      const img = new Image();

      // Marked as ready on arrival, so stepping to it shows no spinner. No
      // redraw here: nothing on screen changes until the viewer moves.
      img.onload = () => this.loadedImages.add(url);
      img.src = url;
    });
  }

  startTimer() {
    this.stopTimer();
    this.progress  = 0;
    this.startTime = Date.now();
    this.paused    = false;

    // A video sets its own pace: the bar follows playback, so buffering
    // holds it, and 'ended' moves on instead of a fixed duration.
    if (this.playsVideo()) {
      this.playVideo();

      this.tickInterval = setInterval(() => {
        const video = this.videoEl;

        if (this.paused || !video || !video.duration) return;

        this.progress = Math.min((video.currentTime / video.duration) * 100, 100);
        m.redraw();
      }, 50);

      return;
    }

    this.tickInterval = setInterval(() => {
      if (this.paused) return;
      const elapsed = Date.now() - this.startTime;
      this.progress = Math.min((elapsed / this.duration) * 100, 100);
      m.redraw();

      if (this.progress >= 100) {
        // An unanswered poll holds the story instead of moving on: reading the
        // question and choosing takes longer than looking at a picture, and a
        // poll that scrolls past unnoticed is the reason people did not know it
        // was there. The viewer still advances by tapping.
        if (this.hasUnansweredPoll()) {
          this.pauseTimer();

          return;
        }

        this.nextStory();
      }
    }, 50); // 50ms tick for smooth animation
  }

  stopTimer() {
    if (this.tickInterval) {
      clearInterval(this.tickInterval);
      this.tickInterval = null;
    }
  }

  pauseTimer() {
    this.paused = true;

    if (this.playsVideo() && this.videoEl) this.videoEl.pause();
  }

  resumeTimer() {
    if (this.paused && this.playsVideo()) {
      this.paused = false;

      // Not before it is ready: the first play belongs to videoReady().
      if (this.videoEl && this.videoEl.isConnected && this.readyVideoId === this.currentStory().id) {
        this.videoEl.play().catch(() => {});
      }

      return;
    }

    if (this.paused) {
      // Adjust start time to account for the pause duration
      // A simpler approach is just resuming progress from current %, we'll do an approximation:
      const timeRemaining = this.duration - (this.progress / 100) * this.duration;
      this.startTime = Date.now() - (this.duration - timeRemaining);
      this.paused = false;
    }
  }

  // ── Navigation ────────────────────────────────────────────────────────────

  nextStory() {
    const group = this.currentGroup();

    // Still inside this user's stories.
    if (group && this.storyIndex < group.stories.length - 1) {
      this.goTo(this.groupIndex, this.storyIndex + 1);
      return;
    }

    // Move on to the next user, or close after the last one.
    if (this.groupIndex < this.attrs.groups.length - 1) {
      this.goTo(this.groupIndex + 1, 0);
    } else {
      this.attrs.onclose();
    }
  }

  prevStory() {
    if (this.storyIndex > 0) {
      this.goTo(this.groupIndex, this.storyIndex - 1);
      return;
    }

    // Back into the previous user, landing on their last story.
    if (this.groupIndex > 0) {
      const previous = this.attrs.groups[this.groupIndex - 1];
      this.goTo(this.groupIndex - 1, Math.max(0, previous.stories.length - 1));
      return;
    }

    // Already at the very first story — restart it.
    this.startTimer();
  }

  goTo(groupIndex, storyIndex) {
    this.groupIndex      = groupIndex;
    this.storyIndex      = storyIndex;
    this.showViewersList = false;
    // Belongs to the story it was opened on, not to the next one.
    this.reactionsOpen   = false;
    // The next video must report canplay for itself.
    this.readyVideoId    = null;

    // A picture that has not arrived holds the countdown: the timer starts from
    // imageReady() instead, so the story is shown for its full length rather
    // than spending part of it on a spinner. Text needs no wait.
    if (this.waitingForImage() || this.playsVideo()) {
      this.stopTimer();
      this.progress = 0;
    } else {
      this.startTimer();
    }

    this.markCurrentViewed();
    this.preloadAround();
    m.redraw();
  }

  /** Whether the story on screen carries a poll this viewer has not answered. */
  hasUnansweredPoll() {
    const story = this.currentStory();
    const poll = story && story.poll;

    return !!(poll && (poll.myAnswer === null || poll.myAnswer === undefined));
  }

  /** Whether the current story is a picture whose bytes are not in yet. */
  waitingForImage() {
    const story = this.currentStory();

    return !!(
      story &&
      story.type === 'image' &&
      story.imageUrl &&
      !this.loadedImages.has(story.imageUrl)
    );
  }

  // ── API Actions ───────────────────────────────────────────────────────────

  /** The owner's heart: the panel, showing only who liked the story. */
  openLikers() {
    this.listMode = 'likers';
    this.reactionsOpen = false;
    this.showViewersList = true;
    this.pauseTimer();
  }

  toggleViewersList() {
    this.listMode = 'all';
    this.showViewersList = !this.showViewersList;
    if (this.showViewersList) {
      this.pauseTimer();
    } else {
      this.resumeTimer();
    }
  }

  /** Opens or closes the choices, and holds the story while they are open. */
  toggleReactions() {
    if (!app.session.user) {
      app.modal.show(require('flarum/forum/components/LogInModal').default);
      return;
    }

    this.reactionsOpen = !this.reactionsOpen;

    // A video keeps playing: stopping its picture and sound for a tap read as
    // the video freezing. A picture or text has nothing visible to stop, so it
    // is held — choosing takes a moment and it should not move on mid-tap.
    if (this.playsVideo()) return;

    if (this.reactionsOpen) {
      this.pauseTimer();
    } else {
      this.resumeTimer();
    }
  }

  /**
   * Send a reaction. The same one again takes it back, a different one replaces
   * it — the server decides which, and its answer is what the strip then shows.
   */
  react(story, reaction) {
    if (!app.session.user) {
      app.modal.show(require('flarum/forum/components/LogInModal').default);
      return;
    }

    // Captured now: the response may land after the viewer has moved on, and
    // by then these indices would point at a different story.
    const groupIdx = this.groupIndex;
    const storyIdx = this.storyIndex;

    this.reactionsOpen = false;
    this.resumeTimer();

    app
      .request({
        method: 'POST',
        url: `/api/happydigital/stories/${story.id}/like`,
        body: { reaction },
      })
      .then((result) => {
        this.attrs.onliketoggle(groupIdx, storyIdx, result);
      });
  }

  markCurrentViewed() {
    const story = this.currentStory();
    if (!story || story.isViewed) return;

    const groupIdx = this.groupIndex;
    const storyIdx = this.storyIndex;

    app
      .request({
        method: 'POST',
        url: `/api/happydigital/stories/${story.id}/view`,
      })
      .then((result) => {
        this.attrs.onviewed(groupIdx, storyIdx, result);
      });
  }

  deleteStory(story) {
    if (!confirm(app.translator.trans('happydigital-stories.forum.delete_confirm'))) return;

    this.pauseTimer();

    const groupIdx = this.groupIndex;
    const storyIdx = this.storyIndex;

    app
      .request({
        method: 'DELETE',
        url: `/api/happydigital/stories/${story.id}`,
      })
      .then(() => {
        this.attrs.ondelete(groupIdx, storyIdx);

        // The parent removed the story; the index may now point past the end.
        const group = this.currentGroup();

        if (!group || group.stories.length === 0) {
          this.attrs.onclose();
          return;
        }

        this.goTo(this.groupIndex, Math.min(this.storyIndex, group.stories.length - 1));
      })
      .catch(() => {
        this.resumeTimer();
      });
  }
}
